// Reusable structured address field group (street / city / state / ZIP)
// with optional search autocomplete + the existing validation affordance.

import { useState } from "react";
import { AddressAutocompleteInput } from "./AddressAutocompleteInput";
import { AddressValidationAffordance } from "./AddressValidationAffordance";
import type { StructuredAddress } from "@/lib/addressValidationClient";

type FieldKey = keyof StructuredAddress;

type Props = {
  value: StructuredAddress;
  onChange: (next: StructuredAddress) => void;
  disabled?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  /** When true, render a search bar that fills all fields on pick. Default true. */
  showSearch?: boolean;
  showValidation?: boolean;
};

const defaultInputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
const defaultLabelClass = "block text-xs text-gray-600 dark:text-zinc-300";

export function StructuredAddressFields({
  value,
  onChange,
  disabled = false,
  inputClassName = defaultInputClass,
  labelClassName = defaultLabelClass,
  showSearch = true,
  showValidation = true,
}: Props) {
  const [searchDraft, setSearchDraft] = useState("");
  const setField = (key: FieldKey, fieldValue: string) => {
    onChange({ ...value, [key]: fieldValue });
  };

  return (
    <div className="space-y-3">
      {showSearch ? (
        <label className={labelClassName}>
          Search address
          <div className="mt-1">
            <AddressAutocompleteInput
              value={searchDraft}
              onChange={setSearchDraft}
              onPick={(pick) => {
                onChange({ ...value, ...pick.normalized });
                setSearchDraft("");
              }}
              disabled={disabled}
              className={inputClassName}
              placeholder="Start typing to search…"
            />
          </div>
        </label>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={labelClassName}>
          Street
          <input
            className={`${inputClassName} mt-1`}
            value={value.street ?? ""}
            onChange={(e) => setField("street", e.target.value)}
            disabled={disabled}
          />
        </label>
        <label className={labelClassName}>
          City
          <input
            className={`${inputClassName} mt-1`}
            value={value.city ?? ""}
            onChange={(e) => setField("city", e.target.value)}
            disabled={disabled}
          />
        </label>
        <label className={labelClassName}>
          State
          <input
            className={`${inputClassName} mt-1`}
            value={value.state ?? ""}
            onChange={(e) => setField("state", e.target.value)}
            placeholder="SC"
            maxLength={16}
            disabled={disabled}
          />
        </label>
        <label className={labelClassName}>
          Postal code
          <input
            className={`${inputClassName} mt-1`}
            value={value.postal_code ?? ""}
            onChange={(e) => setField("postal_code", e.target.value)}
            placeholder="29115"
            maxLength={32}
            disabled={disabled}
          />
        </label>
      </div>

      {showValidation ? (
        <AddressValidationAffordance value={value} onApply={(n) => onChange({ ...value, ...n })} />
      ) : null}
    </div>
  );
}
